import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  createPurchaseRequest,
  fetchStoresForPurchaseRequest,
  selectCreatePurchaseRequestError,
  selectCreatePurchaseRequestStatus,
  selectPurchaseRequestStores,
  selectPurchaseRequestStoresError,
  selectPurchaseRequestStoresStatus,
} from "../features/purchaseRequests/purchaseRequestsSlice";
import "./StockTransferCreatePage.css";

export default function PurchaseRequestCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const wrapRef = useRef(null);

  const [query, setQuery] = useState("");
  const [storeId, setStoreId] = useState(null);
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [formError, setFormError] = useState(null);

  const stores = useSelector(selectPurchaseRequestStores);
  const storesStatus = useSelector(selectPurchaseRequestStoresStatus);
  const storesError = useSelector(selectPurchaseRequestStoresError);
  const createStatus = useSelector(selectCreatePurchaseRequestStatus);
  const createError = useSelector(selectCreatePurchaseRequestError);

  const fromList = useMemo(() => {
    const from = location.state?.from;
    return typeof from === "string" && from.startsWith("/") ? from : null;
  }, [location.state]);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      dispatch(fetchStoresForPurchaseRequest({ search: query }));
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [dispatch, open, query]);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKeyDown(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  const visibleStores = useMemo(() => (stores || []).slice(0, 30), [stores]);
  const canSubmit = useMemo(() => Boolean(storeId) && createStatus !== "saving", [createStatus, storeId]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError(null);
      if (!storeId) {
        setFormError("Нужно выбрать склад (store)");
        return;
      }
      try {
        const created = await dispatch(
          createPurchaseRequest({
            store: Number(storeId),
            comment: String(comment || ""),
          })
        ).unwrap();
        navigate(`/warehouse/purchase-requests/${created?.id}`, { replace: true, state: { from: fromList } });
      } catch {
        // error is in redux
      }
    },
    [comment, dispatch, fromList, navigate, storeId]
  );

  return (
    <div className="stock-transfer-create-page">
      <div className="header">
        <div className="breadcrumbs">
          <Link to={fromList || "/warehouse/purchase-requests"} className="link">
            Запросы на закуп
          </Link>
          <span className="sep">/</span>
          <span className="muted">Новая заявка</span>
        </div>
        <div className="title">Создать заявку на закуп</div>
        <div className="sub-title">
          Создание через <span className="mono">POST /api/product/purchase-requests/</span>.
        </div>
      </div>

      {(createError || storesError || formError) && <div className="error">{formError || createError || storesError}</div>}

      <form onSubmit={onSubmit} className="form">
        <label className="field">
          <div className="label">Склад (store)</div>
          <div className="combo-wrap" ref={wrapRef}>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setStoreId(null);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder="Начни вводить название/адрес…"
              className="input"
            />
            {open && (
              <div className="dropdown">
                <div className="dropdown-hint">{storesStatus === "loading" ? "Загрузка…" : "Выбери склад из списка ниже"}</div>
                {visibleStores.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setStoreId(s.id);
                      setQuery(`${s.name || `Store #${s.id}`}${s.address ? ` — ${s.address}` : ""}`);
                      setOpen(false);
                    }}
                  >
                    <div className="dropdown-title">{s.name || `Store #${s.id}`}</div>
                    <div className="dropdown-sub">
                      {s.address || "—"} · #{s.id}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </label>

        <label className="field">
          <div className="label">Комментарий (comment)</div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Комментарий…"
            className="textarea"
            rows={4}
          />
        </label>

        <div className="actions">
          <button type="submit" className="btn-primary" disabled={!canSubmit}>
            {createStatus === "saving" ? "Создаю…" : "Создать"}
          </button>
          <Link to={fromList || "/warehouse/purchase-requests"} className="btn-ghost">
            Отмена
          </Link>
        </div>
      </form>
    </div>
  );
}
